import { FC, ReactNode } from 'react';

import classNames from 'classnames';
import DisplayTypeDropDown from './DisplayTypeDropDown';
import GroupByDropDown from './GroupByDropDown';
import Pagination from './Pagination';
import { SortByDropDown } from './SortByDropDown';
import { ChangePriceDialog } from './ChangePriceDialog';
import { ButtonSize } from './Button';
import { PriceTier } from '../../../models/PriceTier';
import { BulkActionResponse } from '../../store/api';
import { hasProductSelection } from '../product-selection/ProductSelectionUtils';
import { ProductSelection } from '../product-selection/ProductSelectionTypes';
import { DisplayType } from './displayType/types';
import { GroupingState } from '../../../models/GroupItem';
import { useTranslation } from 'react-i18next';
import { useDisplayFilterSearchParam } from '../utils/useDisplayFilterSearchParam';

type OwnProps = {
    readonly sorting?: ReadonlyArray<{ readonly key: string; readonly label: string }>;
    readonly excludeSorting?: ReadonlyArray<string>;
    readonly grouping?: GroupingState;
    readonly isFilterToggleReplaced?: boolean;
    readonly leftColumnText?: ReactNode;
    readonly hideSelectButtons?: boolean;
    readonly hideSelectAll?: boolean;
    readonly hidePagination?: boolean;
    readonly hideDisplayType?: boolean;
    readonly hideGroupBy?: boolean;
    readonly excludeDisplayTypes?: ReadonlyArray<DisplayType>;
    readonly groupByGroups?: ReadonlyArray<{ readonly key: string; readonly label: string }>;
    readonly loading?: boolean;
    readonly className?: string;
    readonly selectAll?: () => void;
    readonly toggleSelectionMode?: () => void;
    readonly onChangePrice?: (priceTier: PriceTier, availabilityCheck: boolean) => Promise<BulkActionResponse>;
};

type Props = {
    readonly pageSize: number;
    readonly totalProducts: number;
    //TODO replace productSelection with a boolean value
    readonly productSelection?: ProductSelection;
    readonly displayType?: DisplayType;
    readonly sortBy: string;
};

export const FilterBar: FC<Props & OwnProps> = ({
    className,
    displayType,
    excludeDisplayTypes = [],
    excludeSorting = [],
    groupByGroups,
    grouping,
    hideDisplayType,
    hideGroupBy,
    hidePagination,
    hideSelectAll,
    hideSelectButtons,
    isFilterToggleReplaced,
    leftColumnText = '',
    loading,
    onChangePrice,
    pageSize,
    productSelection,
    selectAll,
    sorting,
    toggleSelectionMode,
    totalProducts,
    sortBy
}) => {
    const { t } = useTranslation();
    const { toggleDisplayFilter } = useDisplayFilterSearchParam();

    const leftColumn = isFilterToggleReplaced ? (
        <div className="layout-column-33 fl-left">
            <span className="left-column-header">{leftColumnText}</span>
        </div>
    ) : (
        <div className="layout-column-33 fl-left nowrap" data-testid="filter-bar">
            <a onClick={toggleDisplayFilter} className="fl-left">
                <i className="material-icons fl-left">list</i>
                <div style={{ display: 'inline-block' }}> {t('components.filterBar.filter')}</div>
            </a>
            {totalProducts >= 0 ? (
                <div className="total-products-label fl-left">
                    {t('components.filterBar.products', {
                        count: totalProducts
                    })}
                </div>
            ) : null}
            {totalProducts ? (
                <span className={classNames({ hidden: hideSelectButtons })}>
                    <a onClick={toggleSelectionMode} className="fl-left cold-bgreen">
                        {' '}
                        <div style={{ display: 'inline-block' }}>
                            {hasProductSelection(productSelection) ? t('cancel') : t('select')}
                        </div>
                    </a>
                    <a
                        onClick={selectAll}
                        className={classNames('fl-left', 'cold-bgreen', {
                            hidden: hideSelectAll
                        })}
                    >
                        <div style={{ display: 'inline-block' }}>{t('selectAll')}</div>
                    </a>
                </span>
            ) : null}

            {onChangePrice && !hasProductSelection(productSelection) ? (
                <ChangePriceDialog onChange={onChangePrice} size={ButtonSize.LARGE} />
            ) : null}
        </div>
    );

    if (totalProducts === 0 && !loading) {
        return <div className={classNames('filter-bar', className)}>{leftColumn}</div>;
    }

    return (
        <div className={classNames('filter-bar', className)} data-displayname="FilterBar">
            {leftColumn}
            <div className="layout-column-33 fl-right">
                <SortByDropDown sorting={sorting} excludeSorting={excludeSorting} sortBy={sortBy} />
                {!hideDisplayType && displayType ? (
                    <DisplayTypeDropDown displayType={displayType} excludeDisplayTypes={excludeDisplayTypes} />
                ) : null}
                {!hideGroupBy ? <GroupByDropDown groups={groupByGroups} current={grouping?.key} /> : null}
            </div>
            <div className="layout-column-33 fl-right">
                <Pagination totalItems={totalProducts} pageSize={pageSize} hidden={hidePagination} />
            </div>
        </div>
    );
};

export default FilterBar;
